import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { Search, Database, AlertCircle, RefreshCw, Eye, Edit, Trash2, Plus, AlertTriangle, ChevronDown, ChevronRight, Users, Tag, MapPin, List } from 'lucide-react';

const AllEntriesPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterLocation, setFilterLocation] = useState('');
  const [filterType, setFilterType] = useState('');
  const [selectedFood, setSelectedFood] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [showValidationWarning, setShowValidationWarning] = useState(false);
  
  // Tab management states
  const [activeTab, setActiveTab] = useState('foods'); // 'foods', 'brands', 'locations', 'types'
  
  // Brand management states
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  
  // Location management states
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locationEditForm, setLocationEditForm] = useState({});
  const [editingLocation, setEditingLocation] = useState(false);
  const [deletingLocation, setDeletingLocation] = useState(false);
  const [showAddLocation, setShowAddLocation] = useState(false);
  const [addingLocation, setAddingLocation] = useState(false);
  const [newLocationForm, setNewLocationForm] = useState({ name: '', color: '#FF5733', description: '' });
  
  // Type management states
  const [types, setTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [typeEditForm, setTypeEditForm] = useState({});
  const [editingType, setEditingType] = useState(false);
  const [deletingType, setDeletingType] = useState(false);
  const [showAddType, setShowAddType] = useState(false);
  const [addingType, setAddingType] = useState(false);
  const [newTypeForm, setNewTypeForm] = useState({ name: '', color: '#33FF57', description: '' });


  const [brandEditForm, setBrandEditForm] = useState({});
  const [brandMergeForm, setBrandMergeForm] = useState({});
  const [editingBrand, setEditingBrand] = useState(false);
  const [mergingBrand, setMergingBrand] = useState(false);
  const [deletingBrand, setDeletingBrand] = useState(false);

  // Fetch all food entries
  const { data: foods, isLoading, error, refetch } = useQuery(
    ['allFoods'],
    async () => {
      const apiUrl = import.meta.env.VITE_API_BASE_URL 
        ? `${import.meta.env.VITE_API_BASE_URL}/api/foods?limit=1000`
        : 'https://api.pkutracker.securedeploy.uk/api/foods?limit=1000';
      
      const response = await fetch(apiUrl);
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch foods: ${response.status} ${response.statusText}`);
      }
      
      const result = await response.json();
      return result;
    },
    {
      staleTime: 30000,
      refetchOnWindowFocus: false,
    }
  );

  // Fetch brands
  const { data: brandsData, refetch: refetchBrands } = useQuery(
    ['brands'],
    async () => {
      const apiUrl = import.meta.env.VITE_API_BASE_URL 
        ? `${import.meta.env.VITE_API_BASE_URL}/api/foods/brands`
        : 'https://api.pkutracker.securedeploy.uk/api/foods/brands';
      
      const response = await fetch(apiUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch brands: ${response.status}`);
      }
      
      const result = await response.json();
      setBrands(result.brands || []);
      return result;
    },
    {
      staleTime: 30000,
      refetchOnWindowFocus: false,
    }
  );

  // Fetch locations (food categories)
  const { data: locationsData, refetch: refetchLocations } = useQuery(
    ['locations'],
    async () => {
      const apiUrl = import.meta.env.VITE_API_BASE_URL 
        ? `${import.meta.env.VITE_API_BASE_URL}/api/foods/categories`
        : 'https://api.pkutracker.securedeploy.uk/api/foods/categories';
      
      const response = await fetch(apiUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch locations: ${response.status}`);
      }
      
      const result = await response.json();
      setLocations(result.categories || []);
      return result;
    },
    {
      staleTime: 30000,
      refetchOnWindowFocus: false,
    }
  );

  // Fetch types
  const { data: typesData, refetch: refetchTypes } = useQuery(
    ['types'],
    async () => {
      const apiUrl = import.meta.env.VITE_API_BASE_URL 
        ? `${import.meta.env.VITE_API_BASE_URL}/api/foods/types`
        : 'https://api.pkutracker.securedeploy.uk/api/foods/types';
      
      const response = await fetch(apiUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch types: ${response.status}`);
      }
      
      const result = await response.json();
      setTypes(result.types || []);
      return result;
    },
    {
      staleTime: 30000,
      refetchOnWindowFocus: false,
    }
  );

  // Filter foods based on search term, status, location, and type
  const filteredFoods = (foods?.foods || []).filter(food => {
    const matchesSearch = food.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         food.brand?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         food.barcode?.includes(searchTerm);
    
    const matchesLocation = !filterLocation || (food.location_ids && food.location_ids.includes(filterLocation));
    const matchesType = !filterType || (food.type_ids && food.type_ids.includes(filterType));
    
    let matchesStatus = true;
    if (filterStatus === 'free') matchesStatus = food.protein_exchanges < 1.0;
    else if (filterStatus === 'with-protein') matchesStatus = food.protein_exchanges >= 1.0;
    
    return matchesSearch && matchesStatus && matchesLocation && matchesType;
  });

  // Calculate protein exchanges from protein per serving (1g = 1 exchange)
  const calculateProteinExchanges = (proteinPerServing) => {
    return proteinPerServing ? parseFloat(proteinPerServing).toFixed(1) : 0;
  };

  // Validation functions
  const validateEditForm = () => {
    const errors = {};
    
    if (!editForm.name?.trim()) {
      errors.name = 'Food name is required';
    }
    
    if (!editForm.protein_per_serving || parseFloat(editForm.protein_per_serving) <= 0) {
      errors.protein_per_serving = 'Protein per serving is required and must be greater than 0';
    }
    
    if (!editForm.serving_size || parseFloat(editForm.serving_size) <= 0) {
      errors.serving_size = 'Serving size is required and must be greater than 0';
    }
    
    return errors;
  };

  const validateBrandForm = () => {
    const errors = {};
    
    if (!brandEditForm.name?.trim()) {
      errors.name = 'Brand name is required';
    }
    
    return errors;
  };

  // Brand management functions
  const handleBrandEdit = (brand) => {
    setSelectedBrand(brand);
    setBrandEditForm({ name: brand });
  };

  const handleBrandEditSubmit = async (e) => {
    e.preventDefault();
    
    const errors = validateBrandForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setShowValidationWarning(true);
      return;
    }
    
    setEditingBrand(true);
    try {
      // Update all foods with this brand
      const foodsWithBrand = (foods?.foods || []).filter(food => food.brand === selectedBrand);
      
      for (const food of foodsWithBrand) {
        const apiUrl = import.meta.env.VITE_API_BASE_URL 
          ? `${import.meta.env.VITE_API_BASE_URL}/api/foods/${food.id}`
          : `https://api.pkutracker.securedeploy.uk/api/foods/${food.id}`;
        
        await fetch(apiUrl, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ brand: brandEditForm.name }),
        });
      }
      
      refetch();
      refetchBrands();
      setSelectedBrand(null);
      setBrandEditForm({});
      setValidationErrors({});
      setShowValidationWarning(false);
    } catch (error) {
      console.error('Error updating brand:', error);
      alert(`Failed to update brand: ${error.message}`);
    } finally {
      setEditingBrand(false);
    }
  };

  const handleBrandMerge = (brand) => {
    setSelectedBrand(brand);
    setBrandMergeForm({ targetBrand: '' });
  };

  const handleBrandMergeSubmit = async (e) => {
    e.preventDefault();
    
    if (!brandMergeForm.targetBrand) {
      alert('Please select a target brand to merge into');
      return;
    }
    
    setMergingBrand(true);
    try {
      // Update all foods with the old brand to use the new brand
      const foodsWithBrand = (foods?.foods || []).filter(food => food.brand === selectedBrand);
      
      for (const food of foodsWithBrand) {
        const apiUrl = import.meta.env.VITE_API_BASE_URL 
          ? `${import.meta.env.VITE_API_BASE_URL}/api/foods/${food.id}`
          : `https://api.pkutracker.securedeploy.uk/api/foods/${food.id}`;
        
        await fetch(apiUrl, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ brand: brandMergeForm.targetBrand }),
        });
      }
      
      refetch();
      refetchBrands();
      setSelectedBrand(null);
      setBrandMergeForm({});
    } catch (error) {
      console.error('Error merging brand:', error);
      alert(`Failed to merge brand: ${error.message}`);
    } finally {
      setMergingBrand(false);
    }
  };

  const handleBrandDelete = (brand) => {
    setSelectedBrand(brand);
    setBrandMergeForm({}); // Use this to indicate delete mode
  };

  const confirmBrandDelete = async () => {
    if (!selectedBrand) return;
    
    setDeletingBrand(true);
    try {
      // Remove brand from all foods (set to null)
      const foodsWithBrand = (foods?.foods || []).filter(food => food.brand === selectedBrand);
      
      for (const food of foodsWithBrand) {
        const apiUrl = import.meta.env.VITE_API_BASE_URL 
          ? `${import.meta.env.VITE_API_BASE_URL}/api/foods/${food.id}`
          : `https://api.pkutracker.securedeploy.uk/api/foods/${food.id}`;
        
        await fetch(apiUrl, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ brand: null }),
        });
      }
      
      refetch();
      refetchBrands();
      setSelectedBrand(null);
      setBrandMergeForm({});
    } catch (error) {
      console.error('Error deleting brand:', error);
      alert(`Failed to delete brand: ${error.message}`);
    } finally {
      setDeletingBrand(false);
    }
  };

  const handleRefresh = () => {
    refetch();
    refetchBrands();
    refetchLocations();
    refetchTypes();
  };

  // ===== LOCATION MANAGEMENT HANDLERS =====

  // Debug component state
  React.useEffect(() => {
    console.log('🔍 Component mounted/updated - Current state:');
    console.log('🔍 deletingLocation:', deletingLocation);
    console.log('🔍 selectedLocation:', selectedLocation);
    console.log('🔍 deletingType:', deletingType);
    console.log('🔍 selectedType:', selectedType);
  }, [deletingLocation, selectedLocation, deletingType, selectedType]);

  const handleLocationEdit = (location) => {
    setSelectedLocation(location);
    setLocationEditForm({ name: location.name, color: location.color, description: location.description });
  };

  const handleLocationEditSubmit = async (e) => {
    e.preventDefault();
    
    if (!locationEditForm.name?.trim() || !locationEditForm.color) {
      alert('Name and color are required');
      return;
    }
    
    setEditingLocation(true);
    try {
      const apiUrl = import.meta.env.VITE_API_BASE_URL 
        ? `${import.meta.env.VITE_API_BASE_URL}/api/foods/categories/${selectedLocation.id}`
        : `https://api.pkutracker.securedeploy.uk/api/foods/categories/${selectedLocation.id}`;
      
      const response = await fetch(apiUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: locationEditForm.name,
          color: locationEditForm.color,
          description: locationEditForm.description || null,
        }),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to update: ${response.status}`);
      }
      
      refetchLocations();
      setSelectedLocation(null);
      setLocationEditForm({});
    } catch (error) {
      console.error('Error updating location:', error);
      alert(`Failed to update location: ${error.message}`);
    } finally {
      setEditingLocation(false);
    }
  };

  const handleLocationDelete = (location) => {
    console.log('🔍 handleLocationDelete called with:', location);
    setSelectedLocation(location);
    setDeletingLocation(true);
    console.log('🔍 State set - selectedLocation:', location, 'deletingLocation: true');
  };

  const confirmLocationDelete = async () => {
    console.log('🔍 confirmLocationDelete called');
    if (!selectedLocation) {
      console.log('❌ No selectedLocation, returning early');
      return;
    }
    
    try {
      console.log('🔍 Starting location deletion for:', selectedLocation);
      
      const apiUrl = import.meta.env.VITE_API_BASE_URL 
        ? `${import.meta.env.VITE_API_BASE_URL}/api/foods/categories/${selectedLocation.id}`
        : `https://api.pkutracker.securedeploy.uk/api/foods/categories/${selectedLocation.id}`;
      
      console.log('🔍 DELETE request to:', apiUrl);
      
      const response = await fetch(apiUrl, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      console.log('🔍 Response status:', response.status);
      console.log('🔍 Response headers:', Object.fromEntries(response.headers.entries()));
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Delete failed with status:', response.status, 'Error:', errorText);
        throw new Error(`Failed to delete: ${response.status} - ${errorText}`);
      }
      
      console.log('✅ Location deleted successfully');
      
      // Refresh the locations list
      await refetchLocations();
      
      // Reset state
      setSelectedLocation(null);
      setDeletingLocation(false);
      
      console.log('✅ Location deletion completed and state reset');
      
    } catch (error) {
      console.error('❌ Error deleting location:', error);
      alert(`Failed to delete location: ${error.message}`);
      setDeletingLocation(false);
    }
  };

  const handleAddLocation = async (e) => {
    e.preventDefault();
    
    if (!newLocationForm.name?.trim() || !newLocationForm.color) {
      alert('Name and color are required');
      return;
    }
    
    setAddingLocation(true);
    try {
      const apiUrl = import.meta.env.VITE_API_BASE_URL 
        ? `${import.meta.env.VITE_API_BASE_URL}/api/foods/categories`
        : `https://api.pkutracker.securedeploy.uk/api/foods/categories`;
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newLocationForm.name,
          color: newLocationForm.color,
          description: newLocationForm.description || null,
        }),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to create: ${response.status}`);
      }
      
      refetchLocations();
      setShowAddLocation(false);
      setNewLocationForm({ name: '', color: '#FF5733', description: '' });
    } catch (error) {
      console.error('Error creating location:', error);
      alert(`Failed to create location: ${error.message}`);
    } finally {
      setAddingLocation(false);
    }
  };

  // ===== TYPE MANAGEMENT HANDLERS =====

  const handleTypeEdit = (type) => {
    setSelectedType(type);
    setTypeEditForm({ name: type.name, color: type.color, description: type.description });
  };

  const handleTypeEditSubmit = async (e) => {
    e.preventDefault();
    
    if (!typeEditForm.name?.trim() || !typeEditForm.color) {
      alert('Name and color are required');
      return;
    }
    
    setEditingType(true);
    try {
      const apiUrl = import.meta.env.VITE_API_BASE_URL 
        ? `${import.meta.env.VITE_API_BASE_URL}/api/foods/types/${selectedType.id}`
        : `https://api.pkutracker.securedeploy.uk/api/foods/types/${selectedType.id}`;
      
      const response = await fetch(apiUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: typeEditForm.name,
          color: typeEditForm.color,
          description: typeEditForm.description || null,
        }),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to update: ${response.status}`);
      }
      
      refetchTypes();
      setSelectedType(null);
      setTypeEditForm({});
    } catch (error) {
      console.error('Error updating type:', error);
      alert(`Failed to update type: ${error.message}`);
    } finally {
      setEditingType(false);
    }
  };

  const handleTypeDelete = (type) => {
    console.log('🔍 handleTypeDelete called with:', type);
    setSelectedType(type);
    setDeletingType(true);
    console.log('🔍 State set - selectedType:', type, 'deletingType: true');
  };

  const confirmTypeDelete = async () => {
    console.log('🔍 confirmTypeDelete called');
    if (!selectedType) {
      console.log('❌ No selectedType, returning early');
      return;
    }
    
    try {
      console.log('🔍 Starting type deletion for:', selectedType);
      
      const apiUrl = import.meta.env.VITE_API_BASE_URL 
        ? `${import.meta.env.VITE_API_BASE_URL}/api/foods/types/${selectedType.id}`
        : `https://api.pkutracker.securedeploy.uk/api/foods/types/${selectedType.id}`;
      
      console.log('🔍 DELETE request to:', apiUrl);
      
      const response = await fetch(apiUrl, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      console.log('🔍 Response status:', response.status);
      console.log('🔍 Response headers:', Object.fromEntries(response.headers.entries()));
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Delete failed with status:', response.status, 'Error:', errorText);
        throw new Error(`Failed to delete: ${response.status} - ${errorText}`);
      }
      
      console.log('✅ Type deleted successfully');
      
      // Refresh the types list
      await refetchTypes();
      
      // Reset state
      setSelectedType(null);
      setDeletingType(false);
      
      console.log('✅ Type deletion completed and state reset');
      
    } catch (error) {
      console.error('❌ Error deleting type:', error);
      alert(`Failed to delete type: ${error.message}`);
      setDeletingType(false);
    }
  };

  const handleAddType = async (e) => {
    e.preventDefault();
    
    if (!newTypeForm.name?.trim() || !newTypeForm.color) {
      alert('Name and color are required');
      return;
    }
    
    setAddingType(true);
    try {
      const apiUrl = import.meta.env.VITE_API_BASE_URL 
        ? `${import.meta.env.VITE_API_BASE_URL}/api/foods/types`
        : `https://api.pkutracker.securedeploy.uk/api/foods/types`;
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newTypeForm.name,
          color: newTypeForm.color,
          description: newTypeForm.description || null,
        }),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to create: ${response.status}`);
      }
      
      refetchTypes();
      setShowAddType(false);
      setNewTypeForm({ name: '', color: '#33FF57', description: '' });
    } catch (error) {
      console.error('Error creating type:', error);
      alert(`Failed to create type: ${error.message}`);
    } finally {
      setAddingType(false);
    }
  };

  const handleViewFood = (food) => {
    setSelectedFood(food);
    setShowViewModal(true);
  };

  const handleEditFood = (food) => {
    setSelectedFood(food);
    setEditForm({
      name: food.name,
      brand: food.brand,
      barcode: food.barcode,
      description: food.description,
      protein_per_serving: food.protein_per_100g || food.protein_exchanges, // Use protein per 100g or exchanges
      protein_per_100g: food.protein_per_100g,
      serving_size: food.serving_size,
      contains_aspartame: food.contains_aspartame,
      ingredients: food.ingredients,
      nutrition_facts: food.nutrition_facts,
      location_ids: food.location_ids || [],
      type_ids: food.type_ids || []
    });
    setValidationErrors({});
    setShowValidationWarning(false);
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form before submission
    const errors = validateEditForm();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setShowValidationWarning(true);
      return;
    }
    
    if (!selectedFood) return;
    
    setUpdating(true);
    try {
      // Calculate protein exchanges from protein per serving (1g = 1 exchange)
      const proteinExchanges = calculateProteinExchanges(editForm.protein_per_serving);
      
      const updateData = {
        name: editForm.name,
        brand: editForm.brand || null,
        barcode: editForm.barcode || null,
        description: editForm.description || null,
        protein_exchanges: parseFloat(proteinExchanges),
        protein_per_100g: editForm.protein_per_100g ? parseFloat(editForm.protein_per_100g) : null,
        serving_size: editForm.serving_size ? String(editForm.serving_size) : null,
        contains_aspartame: Boolean(editForm.contains_aspartame),
        ingredients: editForm.ingredients || null,
        nutrition_facts: editForm.nutrition_facts || null,
        location_ids: editForm.location_ids || [],
        type_ids: editForm.type_ids || []
      };
      
      const apiUrl = import.meta.env.VITE_API_BASE_URL 
        ? `${import.meta.env.VITE_API_BASE_URL}/api/foods/${selectedFood.id}`
        : `https://api.pkutracker.securedeploy.uk/api/foods/${selectedFood.id}`;
      
      const response = await fetch(apiUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateData),
      });
      
      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = await response.json();
          if (errorData.error && typeof errorData.error === 'object') {
            errorMessage = errorData.error.message || errorData.error.details || JSON.stringify(errorData.error);
          } else if (errorData.error) {
            errorMessage = errorData.error;
          } else if (errorData.details) {
            errorMessage = errorData.details;
          }
        } catch (e) {
          console.log('Could not parse error response as JSON');
        }
        throw new Error(errorMessage);
      }
      
      // Refresh the list
      refetch();
      setShowEditModal(false);
      setSelectedFood(null);
      setEditForm({});
      setValidationErrors({});
      setShowValidationWarning(false);
    } catch (error) {
      console.error('❌ Update error:', error);
      alert(`Failed to update food: ${error.message}`);
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteFood = (food) => {
    setSelectedFood(food);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!selectedFood) return;
    
    setDeleting(true);
    try {
      const apiUrl = import.meta.env.VITE_API_BASE_URL 
        ? `${import.meta.env.VITE_API_BASE_URL}/api/foods/${selectedFood.id}`
        : `https://api.pkutracker.securedeploy.uk/api/foods/${selectedFood.id}`;
      
      const response = await fetch(apiUrl, {
        method: 'DELETE',
      });
      
      if (!response.ok) {
        throw new Error(`Failed to delete: ${response.status}`);
      }
      
      // Refresh the list
      refetch();
      setShowDeleteModal(false);
      setSelectedFood(null);
    } catch (error) {
      console.error('❌ Delete error:', error);
      alert(`Failed to delete food: ${error.message}`);
    } finally {
      setDeleting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <RefreshCw className="mx-auto h-12 w-12 text-gray-400 animate-spin" />
            <h2 className="mt-4 text-lg font-medium text-gray-900">Loading all entries...</h2>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <AlertCircle className="mx-auto h-12 w-12 text-red-400" />
            <h2 className="mt-4 text-lg font-medium text-gray-900">Error loading entries</h2>
            <p className="mt-2 text-sm text-gray-600">{error.message}</p>
            <button
              onClick={handleRefresh}
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Debug: Show raw data structure
  if (foods && typeof foods === 'object') {
    console.log('🔍 Debug - foods object:', foods);
    console.log('🔍 Debug - foods.foods:', foods.foods);
    console.log('🔍 Debug - foods.foods type:', typeof foods.foods);
    console.log('🔍 Debug - foods.foods length:', foods.foods?.length);
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center">
                <Database className="mr-3 h-8 w-8 text-green-600" />
                Debug & Management Console
              </h1>
              <p className="mt-2 text-gray-600">
                Advanced management and debugging tools for the PKU Tracker database
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={handleRefresh}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <RefreshCw className="mr-2 h-4 w-4" />
                Refresh
              </button>
              <span className="text-sm text-gray-500">
                Total: {foods?.foods?.length || 0} entries
              </span>
            </div>
          </div>
        </div>

        {/* Management Tabs */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab('foods')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'foods'
                    ? 'border-blue-500 text-blue-600' 
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Database className="inline h-5 w-5 mr-2" />
                Food Entries
              </button>
              <button
                onClick={() => setActiveTab('brands')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'brands'
                    ? 'border-blue-500 text-blue-600' 
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Tag className="inline h-5 w-5 mr-2" />
                Brand Management
              </button>
              <button
                onClick={() => setActiveTab('locations')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'locations'
                    ? 'border-blue-500 text-blue-600' 
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <MapPin className="inline h-5 w-5 mr-2" />
                Location Management
              </button>
              <button
                onClick={() => setActiveTab('types')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'types'
                    ? 'border-blue-500 text-blue-600' 
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <List className="inline h-5 w-5 mr-2" />
                Type Management
              </button>
            </nav>
          </div>
        </div>

        {/* Brand Management Section */}
        {activeTab === 'brands' && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Brand Management</h2>
                <p className="text-sm text-gray-600">
                  Manage, edit, merge, and delete brands. Changes will affect all foods using these brands.
                </p>
              </div>
              <button
                onClick={handleRefresh}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                title="Refresh brands"
              >
                <RefreshCw className="h-5 w-5" />
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Brand Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Foods Count
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {brands.map((brand) => {
                    const foodsCount = (foods?.foods || []).filter(food => food.brand === brand).length;
                    const isEditing = selectedBrand === brand && brandEditForm.name;
                    const isMerging = selectedBrand === brand && !brandEditForm.name && brandMergeForm.targetBrand !== undefined;
                    const isDeleting = selectedBrand === brand && !brandEditForm.name && !brandMergeForm.targetBrand;
                    
                    return (
                      <tr key={brand} className="group hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {isEditing ? (
                            <div className="flex items-center space-x-2">
                              <input
                                type="text"
                                value={brandEditForm.name}
                                onChange={(e) => setBrandEditForm({...brandEditForm, name: e.target.value})}
                                className="flex-1 px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Brand name"
                              />
                              <button
                                onClick={handleBrandEditSubmit}
                                disabled={editingBrand}
                                className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 disabled:opacity-50"
                              >
                                {editingBrand ? 'Saving...' : 'Save'}
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedBrand(null);
                                  setBrandEditForm({});
                                }}
                                className="px-2 py-1 bg-gray-600 text-white text-xs rounded hover:bg-gray-700"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : isMerging ? (
                            <div className="flex items-center space-x-2">
                              <span className="text-sm text-gray-600">Merge into:</span>
                              <select
                                value={brandMergeForm.targetBrand}
                                onChange={(e) => setBrandMergeForm({...brandMergeForm, targetBrand: e.target.value})}
                                className="flex-1 px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                              >
                                <option value="">Select target brand</option>
                                {brands.filter(b => b !== brand).map(b => (
                                  <option key={b} value={b}>{b}</option>
                                ))}
                              </select>
                              <button
                                onClick={handleBrandMergeSubmit}
                                disabled={mergingBrand || !brandMergeForm.targetBrand}
                                className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:opacity-50"
                              >
                                {mergingBrand ? 'Merging...' : 'Merge'}
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedBrand(null);
                                  setBrandMergeForm({});
                                }}
                                className="px-2 py-1 bg-gray-600 text-white text-xs rounded hover:bg-gray-700"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : isDeleting ? (
                            <div className="flex items-center space-x-2">
                              <span className="text-sm text-red-600">Delete this brand?</span>
                              <button
                                onClick={confirmBrandDelete}
                                disabled={deletingBrand}
                                className="px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 disabled:opacity-50"
                              >
                                {deletingBrand ? 'Deleting...' : 'Confirm Delete'}
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedBrand(null);
                                  setBrandMergeForm({});
                                }}
                                className="px-2 py-1 bg-gray-600 text-white text-xs rounded hover:bg-gray-700"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-2">
                              <span>{brand}</span>
                              <button
                                onClick={() => handleBrandEdit(brand)}
                                className="text-indigo-600 hover:text-indigo-900 p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Edit Brand"
                              >
                                <Edit className="h-3 w-3" />
                              </button>
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {foodsCount} food{foodsCount !== 1 ? 's' : ''}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex space-x-2">
                            {!isEditing && !isMerging && !isDeleting && (
                              <>
                                <button
                                  onClick={() => handleBrandEdit(brand)}
                                  className="text-indigo-600 hover:text-indigo-900 p-1 rounded"
                                  title="Edit Brand"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleBrandMerge(brand)}
                                  className="text-blue-600 hover:text-blue-900 p-1 rounded"
                                  title="Merge Brand"
                                >
                                  <ChevronDown className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleBrandDelete(brand)}
                                  className="text-red-600 hover:text-red-900 p-1 rounded"
                                  title="Delete Brand"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Location Management Section */}
        {activeTab === 'locations' && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Location Management</h2>
                <p className="text-sm text-gray-600">
                  Manage food storage locations with color-coded labels. Changes will affect all foods using these locations.
                </p>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setShowAddLocation(true)}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                  title="Add new location"
                >
                  <Plus className="h-5 w-5 mr-2" />
                  Add Location
                </button>
                <button
                  onClick={handleRefresh}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  title="Refresh locations"
                >
                  <RefreshCw className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Location Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Color
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Foods Count
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {locations.map((location) => {
                    const foodsCount = (foods?.foods || []).filter(food => food.location_id === location.id).length;
                    const isEditing = selectedLocation === location && locationEditForm.name;
                    
                    return (
                      <tr key={location.id} className="group hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {isEditing ? (
                            <div className="flex items-center space-x-2">
                              <input
                                type="text"
                                value={locationEditForm.name}
                                onChange={(e) => setLocationEditForm({...locationEditForm, name: e.target.value})}
                                className="flex-1 px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Location name"
                              />
                              <input
                                type="color"
                                value={locationEditForm.color}
                                onChange={(e) => setLocationEditForm({...locationEditForm, color: e.target.value})}
                                className="w-8 h-8 border border-gray-300 rounded cursor-pointer"
                                title="Choose color"
                              />
                              <button
                                onClick={handleLocationEditSubmit}
                                disabled={editingLocation}
                                className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 disabled:opacity-50"
                              >
                                {editingLocation ? 'Saving...' : 'Save'}
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedLocation(null);
                                  setLocationEditForm({});
                                }}
                                className="px-2 py-1 bg-gray-600 text-white text-xs rounded hover:bg-gray-700"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-2">
                              <div 
                                className="w-4 h-4 rounded-full border border-gray-300"
                                style={{ backgroundColor: location.color }}
                                title={location.color}
                              />
                              <span>{location.name}</span>
                              <button
                                onClick={() => handleLocationEdit(location)}
                                className="text-indigo-600 hover:text-indigo-900 p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Edit Location"
                              >
                                <Edit className="h-3 w-3" />
                              </button>
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {!isEditing && (
                            <div className="flex items-center space-x-2">
                              <div 
                                className="w-6 h-6 rounded border border-gray-300"
                                style={{ backgroundColor: location.color }}
                                title={location.color}
                              />
                              <span className="text-xs font-mono">{location.color}</span>
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {foodsCount} food{foodsCount !== 1 ? 's' : ''}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex space-x-2">
                            {!isEditing && (
                              <>
                                <button
                                  onClick={() => handleLocationEdit(location)}
                                  className="text-indigo-600 hover:text-indigo-900 p-1 rounded"
                                  title="Edit Location"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => {
                                    console.log('🔍 Delete button clicked for location:', location);
                                    handleLocationDelete(location);
                                  }}
                                  className="text-red-600 hover:text-red-900 p-1 rounded"
                                  title="Delete Location"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Type Management Section */}
        {activeTab === 'types' && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Type Management</h2>
                <p className="text-sm text-gray-600">
                  Manage food types with color-coded labels. Changes will affect all foods using these types.
                </p>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setShowAddType(true)}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                  title="Add new type"
                >
                  <Plus className="h-5 w-5 mr-2" />
                  Add Type
                </button>
                <button
                  onClick={handleRefresh}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  title="Refresh types"
                >
                  <RefreshCw className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Type Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Color
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Foods Count
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {types.map((type) => {
                    const foodsCount = (foods?.foods || []).filter(food => food.type_id === type.id).length;
                    const isEditing = selectedType === type && typeEditForm.name;
                    
                    return (
                      <tr key={type.id} className="group hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {isEditing ? (
                            <div className="flex items-center space-x-2">
                              <input
                                type="text"
                                value={typeEditForm.name}
                                onChange={(e) => setTypeEditForm({...typeEditForm, name: e.target.value})}
                                className="flex-1 px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                placeholder="Type name"
                              />
                              <input
                                type="color"
                                value={typeEditForm.color}
                                onChange={(e) => setTypeEditForm({...typeEditForm, color: e.target.value})}
                                className="w-8 h-8 border border-gray-300 rounded cursor-pointer"
                                title="Choose color"
                              />
                              <button
                                onClick={handleTypeEditSubmit}
                                disabled={editingType}
                                className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 disabled:opacity-50"
                              >
                                {editingType ? 'Saving...' : 'Save'}
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedType(null);
                                  setTypeEditForm({});
                                }}
                                className="px-2 py-1 bg-gray-600 text-white text-xs rounded hover:bg-gray-700"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-2">
                              <div 
                                className="w-4 h-4 rounded-full border border-gray-300"
                                style={{ backgroundColor: type.color }}
                                title={type.color}
                              />
                              <span>{type.name}</span>
                              <button
                                onClick={() => handleTypeEdit(type)}
                                className="text-indigo-600 hover:text-indigo-900 p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Edit Type"
                              >
                                <Edit className="h-3 w-3" />
                              </button>
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {!isEditing && (
                            <div className="flex items-center space-x-2">
                              <div 
                                className="w-6 h-6 rounded border border-gray-300"
                                style={{ backgroundColor: type.color }}
                                title={type.color}
                              />
                              <span className="text-xs font-mono">{type.color}</span>
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {foodsCount} food{foodsCount !== 1 ? 's' : ''}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex space-x-2">
                            {!isEditing && (
                              <>
                                <button
                                  onClick={() => handleTypeEdit(type)}
                                  className="text-indigo-600 hover:text-indigo-900 p-1 rounded"
                                  title="Edit Type"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => {
                                    console.log('🔍 Delete button clicked for type:', type);
                                    handleTypeDelete(type);
                                  }}
                                  className="text-red-600 hover:text-red-900 p-1 rounded"
                                  title="Delete Type"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

                {/* Food Entries Section */}
        {activeTab === 'foods' && (
          <>
            {/* Search and Filters */}
        <div className="mb-6 bg-white rounded-lg shadow p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, brand, or barcode..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-green-500 focus:border-green-500"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
              >
                <option value="all">All Entries</option>
                <option value="free">Free Foods (&lt;1.0g)</option>
                <option value="with-protein">With Protein (≥1.0g)</option>
              </select>
              
              <select
                value={filterLocation || ''}
                onChange={(e) => setFilterLocation(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
              >
                <option value="">All Locations</option>
                {locations.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>
              
              <select
                value={filterType || ''}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
              >
                <option value="">All Types</option>
                {types.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {filteredFoods.length === 0 ? (
            <div className="text-center py-12">
              <Database className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-4 text-lg font-medium text-gray-900">No entries found</h3>
              <p className="mt-2 text-sm text-gray-600">
                {searchTerm || filterStatus !== 'all' 
                  ? 'Try adjusting your search or filters'
                  : 'No food entries in the database yet'
                }
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Food Details
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Protein
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Location & Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Aspartame
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Added
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredFoods.map((food) => (
                    <tr key={food.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{food.name}</div>
                          <div className="text-sm text-gray-500">
                            {food.brand && <span className="mr-2">Brand: {food.brand}</span>}
                            {food.barcode && <span>Barcode: {food.barcode}</span>}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          food.protein_exchanges < 1.0 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-emerald-100 text-emerald-800'
                        }`}>
                          {food.protein_exchanges}g
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col space-y-1">
                          {food.location_ids && food.location_ids.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {food.location_ids.map((locationId) => {
                                const location = locations.find(l => l.id === locationId);
                                return location ? (
                                  <span key={locationId} className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full">
                                    <div 
                                      className="w-3 h-3 rounded-full mr-1 border border-gray-300"
                                      style={{ backgroundColor: location.color }}
                                    />
                                    {location.name}
                                  </span>
                                ) : null;
                              })}
                            </div>
                          )}
                          {food.type_ids && food.type_ids.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {food.type_ids.map((typeId) => {
                                const type = types.find(t => t.id === typeId);
                                return type ? (
                                  <span key={typeId} className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full">
                                    <div 
                                      className="w-3 h-3 rounded-full mr-1 border border-gray-300"
                                      style={{ backgroundColor: type.color }}
                                    />
                                    {type.name}
                                  </span>
                                ) : null;
                              })}
                            </div>
                          )}
                          {(!food.location_ids || food.location_ids.length === 0) && 
                           (!food.type_ids || food.type_ids.length === 0) && (
                            <span className="text-xs text-gray-400">No labels</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          food.contains_aspartame 
                            ? 'bg-red-100 text-red-800' 
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {food.contains_aspartame ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(food.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button 
                            onClick={() => handleViewFood(food)}
                            className="text-green-600 hover:text-green-900"
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button 
                            onClick={() => handleEditFood(food)}
                            className="text-indigo-600 hover:text-indigo-900"
                            title="Edit Food"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button 
                            onClick={() => handleDeleteFood(food)}
                            className="text-red-600 hover:text-red-900"
                            title="Delete Food"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Debug Info */}
        <div className="mt-8 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-yellow-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">Debug Information</h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p><strong>URL:</strong> /debug/all-entries</p>
                <p><strong>Total Entries:</strong> {foods?.foods?.length || 0}</p>
                <p><strong>Filtered Results:</strong> {filteredFoods.length}</p>
                <p><strong>API Endpoint:</strong> {import.meta.env.VITE_API_BASE_URL}/api/foods</p>
                <p><strong>Last Updated:</strong> {new Date().toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>
          </>
        )}
      </div>

      {/* View Food Modal */}
      {showViewModal && selectedFood && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Food Details</h3>
            <div className="space-y-3">
              <div><strong>Name:</strong> {selectedFood.name}</div>
              {selectedFood.brand && <div><strong>Brand:</strong> {selectedFood.brand}</div>}
              {selectedFood.barcode && <div><strong>Barcode:</strong> {selectedFood.barcode}</div>}
              {selectedFood.description && <div><strong>Description:</strong> {selectedFood.description}</div>}
              <div><strong>Protein:</strong> {selectedFood.protein_exchanges} exchanges</div>
              {selectedFood.serving_size && <div><strong>Serving Size:</strong> {selectedFood.serving_size}</div>}
              <div><strong>Aspartame:</strong> {selectedFood.contains_aspartame ? 'Yes' : 'No'}</div>
              {selectedFood.ingredients && <div><strong>Ingredients:</strong> {selectedFood.ingredients}</div>}
              <div><strong>Added:</strong> {new Date(selectedFood.created_at).toLocaleString()}</div>
            </div>
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowViewModal(false)}
                className="btn btn-outline"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Food Modal */}
      {showEditModal && selectedFood && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">Edit Food</h3>
            
            {/* Validation Warning */}
            {showValidationWarning && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <div className="flex items-center space-x-2 text-red-800">
                  <AlertTriangle className="w-5 h-5" />
                  <span className="font-medium">Please fill in all required fields marked with *</span>
                </div>
              </div>
            )}
            
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
                {validationErrors.name && <p className="text-red-500 text-xs mt-1">{validationErrors.name}</p>}
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Brand</label>
                <input
                  type="text"
                  value={editForm.brand || ''}
                  onChange={(e) => setEditForm({...editForm, brand: e.target.value || null})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Locations</label>
                <div className="space-y-2 max-h-32 overflow-y-auto border border-gray-300 rounded-md p-2">
                  {locations.map((location) => (
                    <label key={location.id} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={editForm.location_ids?.includes(location.id) || false}
                        onChange={(e) => {
                          const currentIds = editForm.location_ids || [];
                          if (e.target.checked) {
                            setEditForm({...editForm, location_ids: [...currentIds, location.id]});
                          } else {
                            setEditForm({...editForm, location_ids: currentIds.filter(id => id !== location.id)});
                          }
                        }}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <div 
                        className="w-4 h-4 rounded-full border border-gray-300"
                        style={{ backgroundColor: location.color }}
                        title={location.color}
                      />
                      <span className="text-sm text-gray-900">{location.name}</span>
                    </label>
                  ))}
                  {locations.length === 0 && (
                    <p className="text-sm text-gray-500 italic">No locations available</p>
                  )}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Types</label>
                <div className="space-y-2 max-h-32 overflow-y-auto border border-gray-300 rounded-md p-2">
                  {types.map((type) => (
                    <label key={type.id} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={editForm.type_ids?.includes(type.id) || false}
                        onChange={(e) => {
                          const currentIds = editForm.type_ids || [];
                          if (e.target.checked) {
                            setEditForm({...editForm, type_ids: [...currentIds, type.id]});
                          } else {
                            setEditForm({...editForm, type_ids: currentIds.filter(id => id !== type.id)});
                          }
                        }}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <div 
                        className="w-4 h-4 rounded-full border border-gray-300"
                        style={{ backgroundColor: type.color }}
                        title={type.color}
                      />
                      <span className="text-sm text-gray-900">{type.name}</span>
                    </label>
                  ))}
                  {types.length === 0 && (
                    <p className="text-sm text-gray-500 italic">No types available</p>
                  )}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Barcode</label>
                <input
                  type="text"
                  value={editForm.barcode || ''}
                  onChange={(e) => setEditForm({...editForm, barcode: e.target.value || null})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={editForm.description || ''}
                  onChange={(e) => setEditForm({...editForm, description: e.target.value || null})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="2"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Protein per Serving (g) *</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="50"
                  value={editForm.protein_per_serving}
                  onChange={(e) => setEditForm({...editForm, protein_per_serving: parseFloat(e.target.value)})}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    validationErrors.protein_per_serving ? 'border-red-500 focus:ring-red-500' : ''
                  }`}
                  required
                />
                {validationErrors.protein_per_serving && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.protein_per_serving}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Protein per 100g (g)</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  value={editForm.protein_per_100g || ''}
                  onChange={(e) => setEditForm({...editForm, protein_per_100g: parseFloat(e.target.value) || null})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., 2.5"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Calculated Protein Exchanges</label>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-900">
                      {calculateProteinExchanges(editForm.protein_per_serving)}
                    </div>
                    <div className="text-sm text-blue-700">exchanges</div>
                    <p className="text-xs text-blue-600 mt-1">1g protein = 1 exchange</p>
                  </div>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Serving Size</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  value={editForm.serving_size}
                  onChange={(e) => setEditForm({...editForm, serving_size: parseFloat(e.target.value)})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {validationErrors.serving_size && <p className="text-red-500 text-xs mt-1">{validationErrors.serving_size}</p>}
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="edit-contains-aspartame"
                  checked={editForm.contains_aspartame}
                  onChange={(e) => setEditForm({...editForm, contains_aspartame: e.target.checked})}
                  className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                />
                <label htmlFor="edit-contains-aspartame" className="ml-2 block text-sm text-gray-900">
                  Contains Aspartame
                </label>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ingredients</label>
                <textarea
                  value={editForm.ingredients || ''}
                  onChange={(e) => setEditForm({...editForm, ingredients: e.target.value || null})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="3"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nutrition Facts</label>
                <textarea
                  value={editForm.nutrition_facts || ''}
                  onChange={(e) => setEditForm({...editForm, nutrition_facts: e.target.value || null})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="3"
                />
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="btn btn-outline"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn bg-green-600 hover:bg-green-700 text-white"
                  disabled={updating}
                >
                  {updating ? 'Updating...' : 'Update Food'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedFood && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4 text-red-600">Delete Food</h3>
            <p className="mb-4">
              Are you sure you want to delete <strong>{selectedFood.name}</strong>? 
              This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="btn btn-outline"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="btn bg-red-600 hover:bg-red-700 text-white"
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Location Modal */}
      {showAddLocation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Add New Location</h3>
            <form onSubmit={handleAddLocation} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={newLocationForm.name}
                  onChange={(e) => setNewLocationForm({...newLocationForm, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Kitchen, Pantry, Freezer"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Color *</label>
                <div className="flex items-center space-x-3">
                  <input
                    type="color"
                    value={newLocationForm.color}
                    onChange={(e) => setNewLocationForm({...newLocationForm, color: e.target.value})}
                    className="w-12 h-12 border border-gray-300 rounded cursor-pointer"
                    title="Choose color"
                  />
                  <span className="text-sm text-gray-600">{newLocationForm.color}</span>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={newLocationForm.description}
                  onChange={(e) => setNewLocationForm({...newLocationForm, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="2"
                  placeholder="Optional description"
                />
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddLocation(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                  disabled={addingLocation}
                >
                  {addingLocation ? 'Adding...' : 'Add Location'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Type Modal */}
      {showAddType && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Add New Type</h3>
            <form onSubmit={handleAddType} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={newTypeForm.name}
                  onChange={(e) => setNewTypeForm({...newTypeForm, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Snack, Meal, Beverage"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Color *</label>
                <div className="flex items-center space-x-3">
                  <input
                    type="color"
                    value={newTypeForm.color}
                    onChange={(e) => setNewTypeForm({...newTypeForm, color: e.target.value})}
                    className="w-12 h-12 border border-gray-300 rounded cursor-pointer"
                    title="Choose color"
                  />
                  <span className="text-sm text-gray-600">{newTypeForm.color}</span>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={newTypeForm.description}
                  onChange={(e) => setNewTypeForm({...newTypeForm, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="2"
                  placeholder="Optional description"
                />
              </div>
              
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddType(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                  disabled={addingType}
                >
                  {addingType ? 'Adding...' : 'Add Type'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Location Confirmation Modal */}
      {deletingLocation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          {console.log('🔍 Rendering Delete Location Modal for:', selectedLocation)}
          {console.log('🔍 Current state - deletingLocation:', deletingLocation, 'selectedLocation:', selectedLocation)}
          {console.log('🔍 Modal render count:', Date.now())}
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4 text-red-600">Delete Location</h3>
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete the location "<strong>{selectedLocation?.name}</strong>"?
              <br /><br />
              <span className="text-red-600 font-medium">Warning:</span> This will remove this location from all foods that currently use it.
            </p>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  console.log('🔍 Cancel button clicked - resetting state');
                  setDeletingLocation(false);
                  setSelectedLocation(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  console.log('🔍 Force Reset button clicked');
                  setDeletingLocation(false);
                  setSelectedLocation(null);
                }}
                className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              >
                Force Reset
              </button>
              <button
                onClick={() => {
                  console.log('🔍 Delete confirmation button clicked');
                  console.log('🔍 Current deletingLocation state:', deletingLocation);
                  confirmLocationDelete();
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                disabled={false}
              >
                {deletingLocation ? 'Deleting...' : 'Delete Location'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Type Confirmation Modal */}
      {deletingType && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          {console.log('🔍 Rendering Delete Type Modal for:', selectedType)}
          {console.log('🔍 Current state - deletingType:', deletingType, 'selectedType:', selectedType)}
          {console.log('🔍 Modal render count:', Date.now())}
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4 text-red-600">Delete Type</h3>
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete the type "<strong>{selectedType?.name}</strong>"?
              <br /><br />
              <span className="text-red-600 font-medium">Warning:</span> This will remove this type from all foods that currently use it.
            </p>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  console.log('🔍 Cancel button clicked - resetting state');
                  setDeletingType(false);
                  setSelectedType(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  console.log('🔍 Force Reset button clicked');
                  setDeletingType(false);
                  setSelectedType(null);
                }}
                className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              >
                Force Reset
              </button>
              <button
                onClick={() => {
                  console.log('🔍 Delete confirmation button clicked');
                  console.log('🔍 Current deletingType state:', deletingType);
                  confirmTypeDelete();
                }}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                disabled={false}
              >
                {deletingType ? 'Deleting...' : 'Delete Type'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AllEntriesPage;
